import { t } from '@glory/i18n';
import { Command } from 'cmdk';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { type ReactNode, useMemo, useState } from 'react';

import { dialogMotionClass, overlayClass } from '../components/dialog.tsx';
import { Shortcut } from '../components/kbd.tsx';
import { Skeleton } from '../components/skeleton.tsx';
import { cn } from '../lib/cn.ts';
import { matchesWords } from '../lib/text-match.ts';
import { hotkeyLabel } from './hotkeys.ts';

export interface CommandEntry<P extends string = string> {
  readonly id: string;
  readonly title: string;
  readonly group: 'pages' | 'commands';
  readonly icon?: ReactNode;
  readonly keywords?: readonly string[];
  /** Сочетание клавиш для подписи: 'g t'. */
  readonly hotkey?: string;
  /** Разрешение, без которого страница или команда не показывается (см. `allowedFor`); тип задаёт приложение. */
  readonly permission?: P;
  readonly perform: () => void;
}

/** Реестр страниц и команд, меню: оставить то, на что у пользователя есть право. Без `permission` — видно всем. */
export function allowedFor<T extends { readonly permission?: string }>(
  items: readonly T[],
  can: (permission: NonNullable<T['permission']>) => boolean,
): T[] {
  return items.filter((item) => item.permission === undefined || can(item.permission));
}

export interface SearchResult {
  readonly id: string;
  readonly title: string;
  readonly subtitle?: string;
  readonly icon?: ReactNode;
  /** Заголовок группы («Чаты»); без него — общая группа «Найдено». Группы идут в порядке первого результата. */
  readonly group?: string;
  readonly perform: () => void;
}

/** Совпадение по всем словам запроса в названии и ключевых словах. */
export function matchesQuery(entry: Pick<CommandEntry, 'title' | 'keywords'>, query: string): boolean {
  return matchesWords([entry.title, ...(entry.keywords ?? [])], query);
}

const itemClass =
  'flex h-10 cursor-default items-center gap-2.5 rounded-md px-2.5 font-semibold select-none ' +
  'data-[selected=true]:bg-selected data-[selected=true]:shadow-[inset_3px_0_0_var(--g-accent)] [&_svg]:size-4 [&_svg]:text-muted';

const groupClass =
  '[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 ' +
  '[&_[cmdk-group-heading]]:text-[0.6875rem] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:tracking-[0.05em] ' +
  '[&_[cmdk-group-heading]]:text-muted [&_[cmdk-group-heading]]:uppercase';

/**
 * Палитра ⌘K: поиск по объектам (с сервера) и по реестру страниц и команд,
 * отфильтрованному по правам. Выбор — Enter, навигация — ↑↓.
 */
export function CommandPalette({
  open,
  onOpenChange,
  entries,
  results,
  searching = false,
  query,
  onQueryChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: readonly CommandEntry[];
  results: readonly SearchResult[];
  searching?: boolean;
  query: string;
  onQueryChange: (query: string) => void;
}) {
  const [selected, setSelected] = useState('');
  const filtered = useMemo(() => entries.filter((e) => matchesQuery(e, query)), [entries, query]);
  const pages = filtered.filter((e) => e.group === 'pages');
  const commands = filtered.filter((e) => e.group === 'commands');
  const resultGroups = useMemo(() => {
    const groups = new Map<string, SearchResult[]>();
    for (const result of results) {
      const heading = result.group ?? t('shell.groupResults');
      groups.set(heading, [...(groups.get(heading) ?? []), result]);
    }
    return [...groups];
  }, [results]);

  const run = (perform: () => void) => {
    onOpenChange(false);
    onQueryChange('');
    perform();
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={overlayClass} />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            'fixed top-[12vh] left-1/2 z-[var(--g-float-z)] w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden',
            'rounded-lg border-thick border-strong bg-surface shadow-float',
            dialogMotionClass,
          )}
        >
          <DialogPrimitive.Title className="sr-only">{t('shell.search')}</DialogPrimitive.Title>
          <Command shouldFilter={false} loop value={selected} onValueChange={setSelected}>
            <Command.Input
              value={query}
              onValueChange={onQueryChange}
              placeholder={t('shell.commandPlaceholder')}
              className="h-12 w-full border-b border-border-soft bg-transparent px-4 text-lead font-medium outline-none placeholder:text-muted"
            />
            {/* Высота списка меняется плавно вместе с результатами: cmdk меряет содержимое без отступов. */}
            <Command.List className="h-[calc(var(--cmdk-list-height)+0.75rem)] max-h-[min(60vh,440px)] overflow-y-auto p-1.5 transition-[height] duration-150 ease-out-quick">
              {searching && results.length === 0 ? (
                <Command.Loading>
                  <div className="flex flex-col gap-2 p-2.5" aria-label={t('shell.commandSearching')}>
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-4 w-1/3" />
                  </div>
                </Command.Loading>
              ) : null}
              {!searching && results.length === 0 && filtered.length === 0 ? (
                <div className="px-3 py-6 text-center text-muted">{t('shell.commandEmpty')}</div>
              ) : null}
              {resultGroups.map(([heading, group]) => (
                <Command.Group key={heading} heading={heading} className={groupClass}>
                  {group.map((r) => (
                    <Command.Item
                      key={r.id}
                      value={`result:${r.id}`}
                      onSelect={() => {
                        run(r.perform);
                      }}
                      className={itemClass}
                    >
                      {r.icon}
                      <span className="min-w-0 flex-1 truncate">{r.title}</span>
                      {r.subtitle ? (
                        <span className="truncate text-small font-medium text-muted">{r.subtitle}</span>
                      ) : null}
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
              {pages.length > 0 ? (
                <Command.Group heading={t('shell.groupPages')} className={groupClass}>
                  {pages.map((e) => (
                    <Command.Item
                      key={e.id}
                      value={`page:${e.id}`}
                      onSelect={() => {
                        run(e.perform);
                      }}
                      className={itemClass}
                    >
                      {e.icon}
                      <span className="flex-1">{e.title}</span>
                      {e.hotkey ? <Shortcut keys={hotkeyLabel(e.hotkey)} /> : null}
                    </Command.Item>
                  ))}
                </Command.Group>
              ) : null}
              {commands.length > 0 ? (
                <Command.Group heading={t('shell.groupCommands')} className={groupClass}>
                  {commands.map((e) => (
                    <Command.Item
                      key={e.id}
                      value={`command:${e.id}`}
                      onSelect={() => {
                        run(e.perform);
                      }}
                      className={itemClass}
                    >
                      {e.icon}
                      <span className="flex-1">{e.title}</span>
                      {e.hotkey ? <Shortcut keys={hotkeyLabel(e.hotkey)} /> : null}
                    </Command.Item>
                  ))}
                </Command.Group>
              ) : null}
            </Command.List>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
